/**
 * Multi-step onboarding wizard for new tenants.
 *
 * Triggered the first time a brand-new admin lands on the Dashboard. The
 * wizard guides them through the 4 fundamental configuration steps that
 * every business needs before they can sell:
 *
 *   1. Datos de la empresa (nombre + cédula + tipo).
 *   2. CEDI (dirección + coordenadas para optimización de rutas).
 *   3. Logo (visible en facturas + catálogo PDF + catálogo público).
 *   4. Carga inicial (descarga planilla / sube planilla / sigue manual).
 *
 * State is persisted on each "Siguiente" so the user can abandon and
 * resume later without losing progress.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Building2, MapPin, ImagePlus, Upload, Check, Loader2, Sparkles, Download, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";

const STEPS = [
  { key: "company", label: "Empresa", Icon: Building2 },
  { key: "cedi",    label: "CEDI",    Icon: MapPin },
  { key: "logo",    label: "Logo",    Icon: ImagePlus },
  { key: "data",    label: "Datos",   Icon: Upload },
  { key: "seller",  label: "Vendedor", Icon: UserPlus },
];

export default function OnboardingWizard({ open, onOpenChange, company, onCompanyChange }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  // Step 5 — Invitar primer vendedor (opcional). State lives at the wizard
  // level so the user can navigate back/forward without losing the email
  // they typed.
  const [sellerEmail, setSellerEmail] = useState("");
  const [sellerName, setSellerName] = useState("");
  const [sellerPassword, setSellerPassword] = useState("");
  const [creatingSeller, setCreatingSeller] = useState(false);
  const [createdSellers, setCreatedSellers] = useState([]); // [{email, display_name}]
  const fileInputRef = useRef(null);
  const importFileRef = useRef(null);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const saveCompany = async (patch) => {
    const next = { ...company, ...patch };
    onCompanyChange(next);
    setSaving(true);
    try {
      await api.put("/company", next);
    } catch {
      toast.error("Error guardando datos de empresa");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/company/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onCompanyChange({ ...company, logo_url: data.logo_url });
      toast.success("Logo cargado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir el logo");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding/template/excel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("fail");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.setAttribute("download", "plantilla_onboarding_minegocio.xlsx");
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Plantilla descargada");
    } catch {
      toast.error("Error al descargar plantilla");
    }
  };

  const uploadImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/onboarding/import-all", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { customers_imported = 0, employees_imported = 0, sellers_created = [] } = data || {};
      toast.success(`Importados ${customers_imported} clientes, ${employees_imported} colaboradores, ${sellers_created.length} vendedores`);
      // Advance to the seller invitation step instead of finishing —
      // imports that already brought sellers will see them prefilled
      // in `createdSellers` so the user knows it's optional.
      if (sellers_created.length) {
        setCreatedSellers(sellers_created.map((nm) => ({ email: "", display_name: nm })));
      }
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en el onboarding");
    } finally {
      setImporting(false);
    }
  };

  const finish = () => {
    localStorage.setItem("onboarding_wizard_done", "1");
    onOpenChange(false);
    window.location.reload();
  };

  const skip = () => {
    localStorage.setItem("onboarding_wizard_done", "1");
    onOpenChange(false);
  };

  // Per-step validation: only "Siguiente" is enabled when the required fields
  // are filled. CEDI/Logo/Data are all optional so they always allow next.
  const canAdvance = () => {
    if (step === 0) return (company.nombre_registrado || "").trim().length >= 2;
    return true;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => o ? onOpenChange(true) : skip()}>
      <DialogContent className="max-w-2xl rounded-sm" data-testid="onboarding-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            Configuración guiada · Paso {step + 1} de {STEPS.length}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-2 -mt-2">
          {STEPS.map((s, i) => (
            <div key={s.key}
              className={`flex-1 h-1 rounded-sm transition-colors ${i <= step ? "bg-[#0B1F3A]" : "bg-slate-200"}`}
              data-testid={`onboarding-step-bar-${i}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-600 mb-2">
          {(() => {
            const { Icon, label } = STEPS[step];
            return (<><Icon className="h-4 w-4 text-[#0B1F3A]" /><span className="font-semibold">{label}</span></>);
          })()}
        </div>

        {step === 0 && (
          <div className="space-y-3" data-testid="onboarding-step-company">
            <p className="text-sm text-slate-600">
              Empezamos con los datos básicos de tu negocio. Aparecen en facturas, recibos y el catálogo público.
            </p>
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Nombre del negocio *</label>
              <Input
                value={company.nombre_registrado || ""}
                onChange={(e) => onCompanyChange({ ...company, nombre_registrado: e.target.value })}
                placeholder="Ej: Pulpería La Esquina S.A."
                className="rounded-sm"
                data-testid="onboarding-company-name"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Tipo de cédula</label>
                <Select
                  value={company.cedula_tipo || "juridica"}
                  onValueChange={(v) => onCompanyChange({ ...company, cedula_tipo: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="onboarding-cedula-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="juridica">Jurídica</SelectItem>
                    <SelectItem value="fisica">Física</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Número de cédula</label>
                <Input
                  value={company.cedula || ""}
                  onChange={(e) => onCompanyChange({ ...company, cedula: e.target.value })}
                  placeholder="3-101-XXXXXX"
                  className="rounded-sm"
                  data-testid="onboarding-cedula"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">WhatsApp / teléfono principal</label>
              <Input
                value={company.whatsapp || company.telefono1 || ""}
                onChange={(e) => onCompanyChange({ ...company, whatsapp: e.target.value, telefono1: e.target.value })}
                placeholder="+506 8888 8888"
                className="rounded-sm"
                data-testid="onboarding-whatsapp"
              />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3" data-testid="onboarding-step-cedi">
            <p className="text-sm text-slate-600">
              El CEDI es tu centro de distribución / local principal. Necesitamos un link de Google Maps con coordenadas para optimizar rutas.
            </p>
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Dirección del CEDI</label>
              <Textarea
                value={company.direccion_cedi || ""}
                onChange={(e) => onCompanyChange({ ...company, direccion_cedi: e.target.value })}
                placeholder="200 m sur de la iglesia, frente al parque…"
                className="rounded-sm"
                rows={2}
                data-testid="onboarding-cedi-address"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Link de Google Maps</label>
              <Input
                value={company.cedi_maps_link || ""}
                onChange={(e) => onCompanyChange({ ...company, cedi_maps_link: e.target.value })}
                placeholder="https://www.google.com/maps/place/.../@9.9281,-84.0907,..."
                className="rounded-sm"
                data-testid="onboarding-cedi-maps-link"
              />
              <p className="text-[11px] text-slate-500 mt-1">Pegá el link <strong>largo</strong> de la barra de direcciones de Google Maps (no el corto maps.app.goo.gl).</p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="onboarding-step-logo">
            <p className="text-sm text-slate-600">
              Subí tu logo para que aparezca en facturas, el catálogo PDF y el catálogo público. Es opcional pero recomendado.
            </p>
            <div className="flex items-center gap-4">
              <div className="h-24 w-24 shrink-0 rounded-sm bg-slate-100 border border-slate-200 grid place-items-center overflow-hidden">
                {company.logo_url ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${company.logo_url}?v=${Date.now()}`}
                    alt="Logo"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <ImagePlus className="h-8 w-8 text-slate-300" />
                )}
              </div>
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={uploadLogo}
                  data-testid="onboarding-logo-input"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="rounded-sm"
                  data-testid="onboarding-logo-upload"
                >
                  {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  {company.logo_url ? "Cambiar logo" : "Subir logo"}
                </Button>
                <p className="text-[11px] text-slate-500 mt-2">PNG con fondo transparente, cuadrado, hasta 5 MB.</p>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="onboarding-step-data">
            <p className="text-sm text-slate-600">
              Último paso. Tenés 3 maneras de empezar a usar MiNegocio:
            </p>
            <div className="grid grid-cols-1 gap-2">
              <button
                onClick={downloadTemplate}
                className="border border-slate-200 rounded-sm p-3 text-left hover:border-[#0B1F3A] transition-colors flex items-center gap-3"
                data-testid="onboarding-download-template"
              >
                <Download className="h-5 w-5 text-[#0B1F3A] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">1. Descargar planilla Excel</p>
                  <p className="text-xs text-slate-500">Llenala con tus clientes, vendedores y colaboradores y subila en el siguiente paso.</p>
                </div>
              </button>

              <input
                ref={importFileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={uploadImport}
                data-testid="onboarding-import-input"
              />
              <button
                onClick={() => importFileRef.current?.click()}
                disabled={importing}
                className="border border-slate-200 rounded-sm p-3 text-left hover:border-emerald-400 transition-colors flex items-center gap-3 disabled:opacity-50"
                data-testid="onboarding-import-upload"
              >
                {importing ? <Loader2 className="h-5 w-5 text-emerald-700 shrink-0 animate-spin" /> : <Upload className="h-5 w-5 text-emerald-700 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">2. Subir planilla completada</p>
                  <p className="text-xs text-slate-500">{importing ? "Importando…" : "Importá clientes + vendedores + colaboradores de una sola vez."}</p>
                </div>
              </button>

              <Link
                to="/customers"
                onClick={() => setStep(4)}
                className="border border-slate-200 rounded-sm p-3 text-left hover:border-amber-400 transition-colors flex items-center gap-3"
                data-testid="onboarding-manual"
              >
                <ArrowRight className="h-5 w-5 text-amber-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">3. Empezar manualmente</p>
                  <p className="text-xs text-slate-500">Ir a Clientes y dar de alta uno por uno. (Te llevamos a invitar un vendedor antes.)</p>
                </div>
              </Link>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3" data-testid="onboarding-step-seller">
            <p className="text-sm text-slate-600">
              ¿Tenés alguien que tome pedidos en la calle? Invitalo como <strong>vendedor</strong>. Acceden con su propio email y solo ven a sus clientes asignados (no ven tu inventario ni los precios de costo).
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Nombre del vendedor</label>
                <Input
                  value={sellerName}
                  onChange={(e) => setSellerName(e.target.value)}
                  placeholder="Ej: Ana Solís"
                  className="rounded-sm"
                  data-testid="onboarding-seller-name"
                />
                <p className="text-[11px] text-slate-500 mt-1">Tiene que coincidir EXACTO con el campo "Vendedor asignado" del cliente.</p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Email</label>
                <Input
                  type="email"
                  value={sellerEmail}
                  onChange={(e) => setSellerEmail(e.target.value)}
                  placeholder="ana@minegocio.com"
                  className="rounded-sm"
                  data-testid="onboarding-seller-email"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Contraseña inicial</label>
                <Input
                  type="text"
                  value={sellerPassword}
                  onChange={(e) => setSellerPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="rounded-sm font-mono"
                  data-testid="onboarding-seller-password"
                />
                <p className="text-[11px] text-slate-500 mt-1">Compartile esta contraseña por WhatsApp; ellos pueden cambiarla después.</p>
              </div>
            </div>

            <Button
              type="button"
              onClick={async () => {
                if (!sellerName.trim() || !sellerEmail.trim() || (sellerPassword || "").length < 6) {
                  toast.error("Completá nombre, email y una contraseña de al menos 6 caracteres");
                  return;
                }
                setCreatingSeller(true);
                try {
                  await api.post("/sellers", {
                    email: sellerEmail.trim(),
                    password: sellerPassword,
                    display_name: sellerName.trim(),
                    name: sellerName.trim(),
                  });
                  setCreatedSellers((prev) => [...prev, { email: sellerEmail.trim(), display_name: sellerName.trim() }]);
                  setSellerEmail(""); setSellerName(""); setSellerPassword("");
                  toast.success("Vendedor invitado");
                } catch (err) {
                  toast.error(err.response?.data?.detail || "No pude crear el vendedor");
                } finally {
                  setCreatingSeller(false);
                }
              }}
              disabled={creatingSeller}
              className="rounded-sm"
              data-testid="onboarding-seller-create"
            >
              {creatingSeller ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
              {creatingSeller ? "Creando…" : "Invitar vendedor"}
            </Button>

            {createdSellers.length > 0 && (
              <div className="border border-emerald-200 bg-emerald-50/50 rounded-sm p-3 space-y-1" data-testid="onboarding-seller-list">
                <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-1">Vendedores invitados</p>
                {createdSellers.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
                    <span className="font-medium">{s.display_name}</span>
                    {s.email && <span className="text-slate-500 text-xs">({s.email})</span>}
                  </div>
                ))}
              </div>
            )}

            <p className="text-[11px] text-slate-500">Podés invitar más vendedores después desde <strong>Admin → Vendedores</strong>.</p>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 mt-4 border-t border-slate-100 pt-3">
          <Button
            variant="ghost"
            onClick={skip}
            data-testid="onboarding-skip"
            className="rounded-sm text-slate-500"
          >
            Saltar por ahora
          </Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                variant="outline"
                onClick={() => setStep(s => Math.max(0, s - 1))}
                className="rounded-sm"
                data-testid="onboarding-back"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Atrás
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button
                onClick={async () => {
                  // Save the latest patch before advancing, so partial progress
                  // survives a closed tab.
                  await saveCompany({});
                  setStep(s => s + 1);
                }}
                disabled={!canAdvance() || saving}
                className="btn-primary rounded-sm"
                data-testid="onboarding-next"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Siguiente <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={finish}
                className="btn-primary rounded-sm"
                data-testid="onboarding-finish"
              >
                <Check className="h-4 w-4 mr-1" /> Finalizar
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
